import { SOCKET_URL } from '@/lib/constants/env';
import { io } from 'socket.io-client';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { EventBus } from '@/event-bus';

export default {
  data() {
    return {
      /**
       * Socket IO instance for the shared mixin
       */
      sharedSocketIO: io(SOCKET_URL),

      /**
       * Indicate if the page using this mixin should only emit events
       *
       * This should be set to true on the page that is using the mixin as all listeners should be registered here
       */
      useSocketEmitOnly: false
    };
  },

  created() {
    if (!this.useSocketEmitOnly) {
      this.sharedSocketIO.on('connect', () => {
        console.info('SOCKET: CONNECTED TO SHARED SOCKET');
      });

      this.sharedSocketIO.on('disconnect', () => {
        console.info('SOCKET: DISCONNECTED FROM SHARED SOCKET');
      });

      // Listen for all events
      this.sharedSocketIO.on('events', async (event) => {
        // If we are dealing with a settings:user event, run these checks
        if (event.app === 'settings:user') {
          // TODO: Remove after fixing socket issue in production environment
          console.log('settings:user', event);
          // log the user out once the logged in user's settings data is updated
          if (!this.user) return;

          if (event.data.id === this.user.id) {
            // Check if forceLogout is set to true
            if (event.data?.forceLogout) {
              // TODO: Remove after fixing socket issue in production environment
              console.log('force logging out user called');
              await this.signOut(this.user.id);
              this.$store.commit('authModule/CLEAR_TOKENS');
              this.$store.commit('authModule/CLEAR_USER');
              this.$router.replace('/auth/login');
              this.displaySnackMessage(
                'Account updated',
                'Your account has been updated. Please log in again to continue.'
              );
            } else {
              // Show popup message to user that their profile was updated
              // And they should log out and log in again for the changes to take effect
              this.SET_PROFILE_UPDATED_DIALOG(true);
            }
          }
        }
        // If we are dealing with a logout-all-users event, run these checks
        if (event.app === 'logout-all-users') {
          // TODO: Remove after fixing socket issue in production environment
          console.log('logout-all-users', event);
          // Check if the user is logged in first
          if (!this.user) return;
          // check if the id in the event data is the same as the logged in user's id
          // If not, log them out
          if (event.data.id !== this.user.id) {
            await this.signOut(this.user.id);
            this.$store.commit('authModule/CLEAR_TOKENS');
            this.$store.commit('authModule/CLEAR_USER');
            this.$router.replace('/auth/login');
            this.displaySnackMessage(
              'System updated',
              'There has been a recent update to the system. Please log in again to continue.'
            );
          }
        }

        // If we are dealing with a logout-single-user event, run these checks
        if (event.app === 'logout-single-user') {
          // Check if the user is logged in first
          if (!this.user) return;
          // check if the logged in user is the user that should be logged out
          if (event.data.id === this.user.id) {
            // TODO: Remove after fixing socket issue in production environment
            console.log('logging out single user called');
            await this.signOut(this.user.id);
            this.$store.commit('authModule/CLEAR_TOKENS');
            this.$store.commit('authModule/CLEAR_USER');
            this.$router.replace('/auth/login');
            this.displaySnackMessage(
              'Account updated',
              'Your account has been updated. Please log in again to continue.'
            );
          }
        }

        // If we are dealing with a role update event, run these checks
        if (event.app === 'role:update') {
          // Check if the user is logged in first
          if (!this.user) return;
          // check if the id in the event data is the same as the logged in user's role id
          // If yes, log them out
          if (event.data.id == this.user.role) {
            await this.signOut(this.user.id);
            this.$store.commit('authModule/CLEAR_TOKENS');
            this.$store.commit('authModule/CLEAR_USER');
            this.$router.replace('/auth/login');
            this.displaySnackMessage(
              'System updated',
              event?.message ||
                'There has been a recent update to the system. Please log in again to continue.'
            );
          }
        }
      });
    }
  },

  computed: {
    ...mapGetters('authModule', ['user'])
  },

  methods: {
    ...mapMutations('coreModule', ['SET_PROFILE_UPDATED_DIALOG']),
    ...mapActions('authModule', ['signOut']),
    /**
     * Broadcast an app event to all online users
     * @param {*} data
     */
    emitEvent({ app, type, message, data }) {
      this.sharedSocketIO.emit('events', {
        app,
        type,
        message,
        data
      });
    },

    /**
     * Display snack message
     * @param { string } title - Message title
     * @param { string } message - Message details
     */
    displaySnackMessage(title, message) {
      EventBus.$emit('SHOW_SNACK', {
        title,
        message,
        color: 'success'
      });
    },

    /**
     * Function used to log out ALL users except the one that triggered the event
     */
    async logoutAllUsers() {
      this.emitEvent({
        app: 'logout-all-users',
        type: 'logout-all-users',
        message: 'Logout all users from the system',
        data: {
          id: this.user.id
        }
      });
      this.displaySnackMessage('Success', 'All users have been logged out.');
    },

    /**
     *  Function used to log out a single user
     * @param {*} userId  - The id of the user to log out
     */
    async logoutSingleUser(userId) {
      this.emitEvent({
        app: 'logout-single-user',
        type: 'logout-single-user',
        message: 'Update user settings',
        data: {
          id: userId
        }
      });
      this.displaySnackMessage('Success', 'The user has been logged out.');
    }
  }
};
